import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { typedResolver } from '@/lib/typedResolver';
import { toast } from 'sonner';
import { getErrorMessage } from '@marine/utils/getErrorMessage';
import { ChevronRight } from 'lucide-react';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@marine/ui/sheet';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Textarea } from '@marine/ui/textarea';
import { Separator } from '@marine/ui/separator';
import { Checkbox } from '@marine/ui/checkbox';
import { formatCurrency } from '@marine/utils/formatters';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@marine/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@marine/ui/select';
import { useCreateQuote, useUpdateQuote } from '../hooks/useQuotes';
import { CustomerSearchCombobox } from './CustomerSearchCombobox';
import { QuickCreateCustomerDialog } from './QuickCreateCustomerDialog';
import { InventoryBoatCombobox } from './InventoryBoatCombobox';
import { ItemCommittedQuotesHint } from './ItemCommittedQuotesHint';
import { AssetItemCombobox } from './AssetItemCombobox';
import { quoteBuilderSchema, type QuoteBuilderSchemaType } from '../validators/quoteSchema';
import type { Quote, QuoteFormData } from '../types/crm.types';
import { useDealershipSettings } from '@/modules/crm/hooks/useDealershipSettings';
import { useLocations } from '@/modules/inventory/hooks/useLocations';
import { useCan, useEmployee } from '@/modules/tenant/hooks/useTenantAuth';

interface QuoteFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote?: Quote;
  /** Pre-selects this boat when opening the sheet for a NEW quote.
      Ignored when editing an existing quote. */
  initialBoatId?: string;
  onCreated?: (id: string) => void;
}

const toNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function QuoteFormSheet({ open, onOpenChange, quote, initialBoatId, onCreated }: QuoteFormSheetProps) {
  const isEditing = !!quote;
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [showCoBuyerQuickCreate, setShowCoBuyerQuickCreate] = useState(false);
  const [includeTradeInDetails, setIncludeTradeInDetails] = useState(false);

  const createQuote = useCreateQuote();
  const updateQuote = useUpdateQuote();
  const { data: settings } = useDealershipSettings();
  const { data: locationsData, isLoading: isLoadingLocations } = useLocations();
  const employee = useEmployee();
  // #1206 (D-08): the server refuses any boat_cost (even 0) from a viewer
  // without finance.cost.view, so a non-holder's form never defaults, shows
  // or sends it. UX only; UpdateQuoteRequest stays the authority.
  const canViewCost = useCan('finance.cost.view');
  const locations = useMemo(() => locationsData ?? [], [locationsData]);
  const defaultLocationId = useMemo(() => {
    if (employee?.location_id) {
      return employee.location_id;
    }
    const primary = locations.find((location: { id: string; name: string; is_main?: boolean }) => location.is_main) || locations[0];
    return primary?.id || '';
  }, [employee?.location_id, locations]);

  const defaultFees = useMemo(
    () => ({
      doc_fee: toNumber(settings?.default_doc_fee),
      freight_fee: toNumber(settings?.default_freight_fee),
      prep_fee: toNumber(settings?.default_prep_fee),
    }),
    [settings?.default_doc_fee, settings?.default_freight_fee, settings?.default_prep_fee],
  );

  const form = useForm<QuoteBuilderSchemaType>({
    resolver: typedResolver(quoteBuilderSchema),
    defaultValues: {
      lead_id: undefined,
      customer_id: '',
      has_co_buyer: false,
      co_buyer_customer_id: '',
      location_id: '',
      item_type: 'boat',
      boat_source: 'inventory',
      boat_id: '',
      manual_make: '',
      manual_model: '',
      manual_year: undefined,
      manual_vin_hin: '',
      manual_stock_number: '',
      manual_length_feet: undefined,
      manual_length_inches: undefined,
      title: '',
      description: '',
      boat_price: undefined,
      trailer_price: undefined,
      accessories_total: undefined,
      trade_in_value: undefined,
      tax_amount: undefined,
      trailer_tax: undefined,
      doc_fee: undefined,
      freight_fee: undefined,
      prep_fee: undefined,
      boat_registration_fee: undefined,
      trailer_registration_fee: undefined,
      boat_cost: undefined,
      trade_in_year: undefined,
      trade_in_make: '',
      trade_in_model: '',
      trade_in_vin_hin: '',
      trade_in_acv: undefined,
      trade_in_allowance: undefined,
      trade_in_payoff: undefined,
      down_payment: undefined,
      loan_term_months: undefined,
      interest_rate: undefined,
      valid_from: '',
      valid_until: '',
    },
  });
  
  // Reset form when dialog opens/closes or quote changes
  useEffect(() => {
    if (!open) return;

    if (quote && quote.customer) {
      const boatSource = quote.inventory_item_id || quote.boat_id || quote.boat ? 'inventory' : quote.boat_snapshot ? 'manual' : 'inventory';
      const manualSnapshot = quote.boat_snapshot || {};
      // #434 — the trade_ins relation is the only source; the snapshot blob
      // never carries a copy.
      const tradeIn = quote.trade_ins?.[0];

      setIncludeTradeInDetails(Boolean(tradeIn));

      form.reset({
        lead_id: quote.lead_id || undefined,
        customer_id: quote.customer.id,
        has_co_buyer: !!quote.co_buyer_customer_id,
        co_buyer_customer_id: quote.co_buyer_customer_id || '',
        location_id: quote.location_id || quote.lead?.location_id || defaultLocationId || '',
        item_type: quote.item_type ?? 'boat',
        boat_source: boatSource,
        boat_id: quote.inventory_item_id || quote.boat_id || quote.boat?.id || '',
        // #434 — snapshots carry canonical keys only (data migrated + cast
        // guarantees the shape); the form splits length_ft back into ft+in.
        manual_make: manualSnapshot.manufacturer || '',
        manual_model: manualSnapshot.model || '',
        manual_year: manualSnapshot.model_year || undefined,
        manual_vin_hin: manualSnapshot.hin_number || '',
        manual_stock_number: manualSnapshot.inventory_number || '',
        manual_length_feet:
          manualSnapshot.length_ft != null
            ? Math.floor(manualSnapshot.length_ft)
            : undefined,
        manual_length_inches:
          manualSnapshot.length_ft != null
            ? Math.round((manualSnapshot.length_ft % 1) * 12) || undefined
            : undefined,
        title: quote.title,
        description: quote.description || '',
        boat_price: quote.boat_price || 0,
        trailer_price: quote.trailer_price || 0,
        accessories_total: quote.accessories_total || 0,
        trade_in_value: quote.trade_in_value || 0,
        tax_amount: quote.tax_amount || 0,
        trailer_tax: quote.trailer_tax || 0,
        doc_fee: quote.doc_fee || 0,
        freight_fee: quote.freight_fee || 0,
        prep_fee: quote.prep_fee || 0,
        boat_registration_fee: quote.boat_registration_fee || 0,
        trailer_registration_fee: quote.trailer_registration_fee || 0,
        boat_cost: canViewCost ? Number(quote.boat_cost) || 0 : undefined,
        trade_in_year: tradeIn?.year || undefined,
        trade_in_make: tradeIn?.make || '',
        trade_in_model: tradeIn?.model || '',
        trade_in_vin_hin: tradeIn?.vin_hin || '',
        trade_in_acv: tradeIn?.trade_acv || undefined,
        trade_in_allowance: tradeIn?.offered_value || undefined,
        trade_in_payoff: tradeIn?.trade_payoff ?? undefined,
        down_payment: quote.down_payment || undefined,
        loan_term_months: quote.loan_term_months || undefined,
        interest_rate: quote.interest_rate || undefined,
        valid_from: quote.valid_from || new Date().toISOString().split('T')[0],
        valid_until: quote.valid_until || '',
      });
    } else {
      setIncludeTradeInDetails(false);
      form.reset({
        lead_id: undefined,
        customer_id: '',
        has_co_buyer: false,
        co_buyer_customer_id: '',
        location_id: defaultLocationId || '',
        item_type: 'boat',
        boat_source: 'inventory',
        boat_id: initialBoatId ?? '',
        manual_make: '',
        manual_model: '',
        manual_year: undefined,
        manual_vin_hin: '',
        manual_stock_number: '',
        manual_length_feet: undefined,
        manual_length_inches: undefined,
        title: '',
        description: '',
        boat_price: undefined,
        trailer_price: undefined,
        accessories_total: undefined,
        trade_in_value: undefined,
        tax_amount: undefined,
        trailer_tax: undefined,
        doc_fee: undefined,
        freight_fee: undefined,
        prep_fee: undefined,
        boat_registration_fee: undefined,
        trailer_registration_fee: undefined,
        boat_cost: undefined,
        trade_in_year: undefined,
        trade_in_make: '',
        trade_in_model: '',
        trade_in_vin_hin: '',
        trade_in_acv: undefined,
        trade_in_allowance: undefined,
        trade_in_payoff: undefined,
        down_payment: undefined,
        loan_term_months: undefined,
        interest_rate: undefined,
        valid_from: '',
        valid_until: '',
      });
    }
  }, [open, quote, form, defaultLocationId, initialBoatId, canViewCost]);

  useEffect(() => {
    if (!open || quote) {
      return;
    }
    if (!defaultLocationId) {
      return;
    }
    if (!form.getValues('location_id')) {
      form.setValue('location_id', defaultLocationId, { shouldDirty: true });
    }
  }, [open, quote, defaultLocationId, form]);

  useEffect(() => {
    if (!open || quote) {
      return;
    }

    const dirtyFields = form.formState.dirtyFields;

    if (!dirtyFields.doc_fee) {
      const currentDocFee = form.getValues('doc_fee');
      if (currentDocFee === undefined || currentDocFee === null || currentDocFee === 0) {
        form.setValue('doc_fee', defaultFees.doc_fee, { shouldDirty: false });
      }
    }

    if (!dirtyFields.freight_fee) {
      const currentFreightFee = form.getValues('freight_fee');
      if (currentFreightFee === undefined || currentFreightFee === null || currentFreightFee === 0) {
        form.setValue('freight_fee', defaultFees.freight_fee, { shouldDirty: false });
      }
    }

    if (!dirtyFields.prep_fee) {
      const currentPrepFee = form.getValues('prep_fee');
      if (currentPrepFee === undefined || currentPrepFee === null || currentPrepFee === 0) {
        form.setValue('prep_fee', defaultFees.prep_fee, { shouldDirty: false });
      }
    }
  }, [open, quote, defaultFees.doc_fee, defaultFees.freight_fee, defaultFees.prep_fee, form]);

  const handleQuickCreateSuccess = (customerId: string) => {
    form.setValue('customer_id', customerId, { shouldValidate: true, shouldDirty: true });
    form.clearErrors('customer_id');
  };

  const handleCoBuyerQuickCreateSuccess = (customerId: string) => {
    form.setValue('co_buyer_customer_id', customerId, { shouldValidate: true, shouldDirty: true });
    form.clearErrors('co_buyer_customer_id');
  };
  
  const boatSource = form.watch('boat_source');
  const itemType = form.watch('item_type') ?? 'boat';
  // #481 — committed-quote warning target (empty string = nothing selected).
  const selectedItemId = form.watch('boat_id') || '';
  const itemTypeLabel = itemType.charAt(0).toUpperCase() + itemType.slice(1);
  const hasCoBuyer = form.watch('has_co_buyer');

  // Watch pricing fields for real-time calculations
  const boatPrice = toNumber(form.watch('boat_price'));
  const trailerPrice = toNumber(form.watch('trailer_price'));
  const accessoriesTotal = toNumber(form.watch('accessories_total'));
  const tradeInValueInput = toNumber(form.watch('trade_in_value'));
  const tradeInAllowance = toNumber(form.watch('trade_in_allowance'));
  const tradeInPayoff = toNumber(form.watch('trade_in_payoff'));
  const tradeInDetailsValue = tradeInAllowance;
  const tradeInValue = includeTradeInDetails ? tradeInDetailsValue : tradeInValueInput;
  const tradePayoff = includeTradeInDetails ? tradeInPayoff : 0;
  const taxAmount = toNumber(form.watch('tax_amount'));
  const trailerTax = toNumber(form.watch('trailer_tax'));
  const docFee = toNumber(form.watch('doc_fee'));
  const freightFee = toNumber(form.watch('freight_fee'));
  const prepFee = toNumber(form.watch('prep_fee'));
  const boatRegistrationFee = toNumber(form.watch('boat_registration_fee'));
  const trailerRegistrationFee = toNumber(form.watch('trailer_registration_fee'));
  const downPayment = toNumber(form.watch('down_payment'));
  const loanTermMonths = toNumber(form.watch('loan_term_months'));
  const interestRate = toNumber(form.watch('interest_rate'));

  // Calculate totals (match backend calculations)
  const price = useMemo(() => {
    return boatPrice + trailerPrice + accessoriesTotal;
  }, [boatPrice, trailerPrice, accessoriesTotal]);

  const tradeEquity = useMemo(() => {
    return tradeInValue - tradePayoff;
  }, [tradeInValue, tradePayoff]);

  const feesAndTaxes = useMemo(() => {
    return (
      docFee + freightFee + prepFee + boatRegistrationFee + trailerRegistrationFee + taxAmount + trailerTax
    );
  }, [docFee, freightFee, prepFee, boatRegistrationFee, trailerRegistrationFee, taxAmount, trailerTax]);

  const total = useMemo(() => {
    return price - tradeEquity + feesAndTaxes;
  }, [price, tradeEquity, feesAndTaxes]);

  // Calculate monthly payment
  const monthlyPayment = useMemo(() => {
    const financeAmount = total - downPayment;
    if (financeAmount <= 0 || !loanTermMonths || loanTermMonths <= 0) return null;

    if (!interestRate || interestRate === 0) {
      return financeAmount / loanTermMonths;
    }

    const monthlyRate = interestRate / 100 / 12;
    const payment =
      (financeAmount * monthlyRate * Math.pow(1 + monthlyRate, loanTermMonths)) /
      (Math.pow(1 + monthlyRate, loanTermMonths) - 1);

    return payment;
  }, [total, downPayment, loanTermMonths, interestRate]);
  
  const onSubmit = async (data: QuoteBuilderSchemaType) => {
    const tradeInHasDetails = includeTradeInDetails && Boolean(
      data.trade_in_year ||
      data.trade_in_make ||
      data.trade_in_model ||
      data.trade_in_vin_hin ||
      data.trade_in_acv ||
      data.trade_in_allowance ||
      data.trade_in_payoff
    );

    const manualTradeValue = data.trade_in_value ?? 0;
    let tradeIns = undefined as Array<{
      vin_hin?: string;
      year?: number;
      make?: string;
      model?: string;
      trade_acv?: number;
      offered_value?: number;
      trade_payoff?: number;
    }> | undefined;

    if (tradeInHasDetails) {
      tradeIns = [
        {
          vin_hin: data.trade_in_vin_hin || undefined,
          year: data.trade_in_year || undefined,
          make: data.trade_in_make || undefined,
          model: data.trade_in_model || undefined,
          trade_acv: data.trade_in_acv || undefined,
          offered_value: data.trade_in_allowance || undefined,
          trade_payoff: data.trade_in_payoff || undefined,
        },
      ];
    } else if (manualTradeValue > 0) {
      tradeIns = [
        {
          offered_value: manualTradeValue,
        },
      ];
    } else if (isEditing) {
      tradeIns = [];
    }

    // #434 — canonical snapshot keys; feet+inches are folded into
    // length_ft server-side by QuoteSnapshotBuilder::canonicalizeKeys().
    const boatSnapshot =
      data.boat_source === 'manual'
        ? {
            source: 'manual' as const,
            manufacturer: data.manual_make || undefined,
            model: data.manual_model || undefined,
            model_year: data.manual_year || undefined,
            hin_number: data.manual_vin_hin || undefined,
            inventory_number: data.manual_stock_number || undefined,
            length_feet: data.manual_length_feet || undefined,
            length_inches: data.manual_length_inches || undefined,
          }
        : undefined;

    const payload: QuoteFormData = {
      lead_id: data.lead_id || undefined,
      customer_id: data.customer_id,
      co_buyer_customer_id: data.has_co_buyer ? data.co_buyer_customer_id || undefined : undefined,
      boat_source: data.boat_source,
      // SP4 slice 3: cut over to the generic item reference (boat_id frozen).
      inventory_item_id: data.boat_source === 'inventory' ? data.boat_id || undefined : undefined,
      item_type: data.boat_source === 'inventory' ? data.item_type : undefined,
      boat_snapshot: boatSnapshot,
      trade_ins: tradeIns,
      title: data.title,
      description: data.description || undefined,
      boat_price: data.boat_price ?? undefined,
      trailer_price: data.trailer_price ?? undefined,
      accessories_total: data.accessories_total ?? undefined,
      tax_amount: data.tax_amount ?? undefined,
      trailer_tax: data.trailer_tax ?? undefined,
      doc_fee: data.doc_fee ?? undefined,
      freight_fee: data.freight_fee ?? undefined,
      prep_fee: data.prep_fee ?? undefined,
      boat_registration_fee: data.boat_registration_fee ?? undefined,
      trailer_registration_fee: data.trailer_registration_fee ?? undefined,
      boat_cost: data.boat_cost ?? undefined,
      down_payment: data.down_payment ?? undefined,
      loan_term_months: data.loan_term_months ?? undefined,
      interest_rate: data.interest_rate ?? undefined,
      valid_from: data.valid_from ?? undefined,
      valid_until: data.valid_until ?? undefined,
    };

    if (!canViewCost) {
      delete payload.boat_cost;
    }

    try {
      if (isEditing && quote) {
        await updateQuote.mutateAsync({
          id: quote.id.toString(),
          data: payload,
        });
        toast.success('Quote updated successfully');
      } else {
        const created = await createQuote.mutateAsync(payload);
        toast.success('Quote created successfully');
        if (created?.id !== undefined && created?.id !== null) {
          onCreated?.(String(created.id));
        }
      }
      onOpenChange(false);
      form.reset();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Failed to save quote'));
    }
  };
  
  const isPending = createQuote.isPending || updateQuote.isPending;
  
  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent variant="panel" className="lg:w-[900px]">
          {/* Header */}
          <SheetHeader className="border-b py-3 px-5 border-border">
            <SheetTitle className="text-sm font-medium">
              {isEditing ? 'Quick Edit Quote' : 'Create New Quote'}
            </SheetTitle>
          </SheetHeader>

          {/* Body */}
          {/* Part of the sheet's vertical flex chain: without flex/min-h-0 here the
              form is a plain block, SheetBody's flex-1 has nothing to measure
              against and the body cannot scroll. This is what the old
              h-[calc(100dvh-10rem)] on the ScrollArea was really working around. */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-1 flex-col min-h-0">
            <SheetBody className="px-1.5 py-0">
                <div className="px-5 py-5 space-y-5">
                <div className="space-y-5">
                  <div className="text-sm font-semibold">Customer & Quote</div>

                  <div className="flex items-start gap-10">
                    <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                      Customer <span className="text-destructive">*</span>
                    </Label>
                    <div className="flex-1 space-y-1">
                      <CustomerSearchCombobox
                        value={form.watch('customer_id')}
                        onChange={(id) =>
                          form.setValue('customer_id', id || '', { shouldDirty: true, shouldValidate: true })
                        }
                        onQuickCreate={() => setShowQuickCreate(true)}
                      />
                      {form.formState.errors.customer_id && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.customer_id.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-10">
                    <Label className="text-xs font-medium w-32 shrink-0 pt-2">Co-buyer</Label>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="quote_has_co_buyer"
                          checked={hasCoBuyer}
                          onCheckedChange={(checked) => {
                            const isChecked = checked === true;
                            form.setValue('has_co_buyer', isChecked, { shouldDirty: true });
                            if (!isChecked) {
                              form.setValue('co_buyer_customer_id', '', { shouldDirty: true });
                              form.clearErrors('co_buyer_customer_id');
                            }
                          }}
                        />
                        <label
                          htmlFor="quote_has_co_buyer"
                          className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Add a co-buyer to this quote
                        </label>
                      </div>
                      {hasCoBuyer && (
                        <div className="space-y-1">
                          <CustomerSearchCombobox
                            value={form.watch('co_buyer_customer_id')}
                            onChange={(id) =>
                              form.setValue('co_buyer_customer_id', id || '', { shouldDirty: true, shouldValidate: true })
                            }
                            onQuickCreate={() => setShowCoBuyerQuickCreate(true)}
                            placeholder="Search co-buyer..."
                          />
                          {form.formState.errors.co_buyer_customer_id && (
                            <p className="text-xs text-destructive">
                              {form.formState.errors.co_buyer_customer_id.message}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-10">
                    <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                      Title <span className="text-destructive">*</span>
                    </Label>
                    <div className="flex-1 space-y-1">
                      <Input
                        placeholder="e.g., 2024 Boston Whaler Purchase Quote"
                        {...form.register('title')}
                      />
                      {form.formState.errors.title && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.title.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-10">
                    <Label className="text-xs font-medium w-32 shrink-0 pt-2">Description</Label>
                    <Textarea
                      placeholder="Add a short summary or special terms..."
                      {...form.register('description', {
                        setValueAs: (value) => (typeof value === 'string' ? value : ''),
                      })}
                      rows={2}
                      className="flex-1 min-h-[56px]"
                    />
                  </div>

                  <div className="flex items-start gap-10">
                    <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                      Location <span className="text-destructive">*</span>
                    </Label>
                    <div className="flex-1 space-y-1">
                      <Select
                        value={form.watch('location_id') || ''}
                        onValueChange={(value) => form.setValue('location_id', value, { shouldDirty: true })}
                        disabled={isLoadingLocations}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={isLoadingLocations ? 'Loading locations...' : 'Select location'} />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map((location: { id: string; name: string; is_main?: boolean }) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                              {location.is_main ? ' • Primary' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.formState.errors.location_id && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.location_id.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-5">
                  <div className="text-sm font-semibold">Quoted Item</div>

                  {/* SP4 slice 3 — quotes can reference a boat, engine, or trailer.
                      Manual entry stays boat-only, so the source select hides for assets. */}
                  <div className="flex items-start gap-10">
                    <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                      Item Type <span className="text-destructive">*</span>
                    </Label>
                    <div className="flex-1 space-y-1">
                      <Select
                        value={itemType}
                        onValueChange={(value) => {
                          const next = value as 'boat' | 'engine' | 'trailer';
                          form.setValue('item_type', next, { shouldDirty: true });
                          form.setValue('boat_id', '', { shouldDirty: true });
                          if (next !== 'boat') {
                            form.setValue('boat_source', 'inventory', { shouldDirty: true });
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose item type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="boat">Boat</SelectItem>
                          <SelectItem value="engine">Engine</SelectItem>
                          <SelectItem value="trailer">Trailer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {itemType === 'boat' && (
                    <div className="flex items-start gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                        Boat Source <span className="text-destructive">*</span>
                      </Label>
                      <div className="flex-1 space-y-1">
                        <Select
                          value={boatSource || 'inventory'}
                          onValueChange={(value) => {
                            const next = value as 'inventory' | 'manual';
                            form.setValue('boat_source', next, { shouldDirty: true });
                            if (next === 'manual') {
                              form.setValue('boat_id', '', { shouldDirty: true });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose boat source" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inventory">Inventory</SelectItem>
                            <SelectItem value="manual">Manual Entry</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {boatSource === 'inventory' ? (
                    <div className="flex items-start gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0 pt-2">
                        Inventory {itemTypeLabel} <span className="text-destructive">*</span>
                      </Label>
                      <div className="flex-1 space-y-1">
                        {itemType === 'boat' ? (
                          <InventoryBoatCombobox
                            value={form.watch('boat_id')}
                            onChange={(id) =>
                              form.setValue('boat_id', id || '', { shouldDirty: true, shouldValidate: true })
                            }
                          />
                        ) : (
                          <AssetItemCombobox
                            type={itemType}
                            value={form.watch('boat_id')}
                            onChange={(id) =>
                              form.setValue('boat_id', id || '', { shouldDirty: true, shouldValidate: true })
                            }
                          />
                        )}
                        {form.formState.errors.boat_id && (
                          <p className="text-xs text-destructive">
                            {form.formState.errors.boat_id.message}
                          </p>
                        )}
                        {/* #481 — committed-quote warning on item selection */}
                        {selectedItemId ? <ItemCommittedQuotesHint itemId={selectedItemId} /> : null}
                        <p className="text-xs text-muted-foreground">
                          Choose a stocked {itemType} to pull details.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">
                            Year <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            type="number"
                            placeholder="2024"
                            {...form.register('manual_year', { valueAsNumber: true })}
                          />
                          {form.formState.errors.manual_year && (
                            <p className="text-xs text-destructive">
                              {form.formState.errors.manual_year.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">
                            Make <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            placeholder="Boston Whaler"
                            {...form.register('manual_make')}
                          />
                          {form.formState.errors.manual_make && (
                            <p className="text-xs text-destructive">
                              {form.formState.errors.manual_make.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">
                            Model <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            placeholder="280 Outrage"
                            {...form.register('manual_model')}
                          />
                          {form.formState.errors.manual_model && (
                            <p className="text-xs text-destructive">
                              {form.formState.errors.manual_model.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">Stock #</Label>
                          <Input
                            placeholder="ST-2301"
                            {...form.register('manual_stock_number')}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">VIN / HIN</Label>
                          <Input
                            placeholder="US-XYZ12345A424"
                            {...form.register('manual_vin_hin')}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs font-medium">Length (ft)</Label>
                            <Input
                              type="number"
                              placeholder="28"
                              {...form.register('manual_length_feet', { valueAsNumber: true })}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs font-medium">Length (in)</Label>
                            <Input
                              type="number"
                              placeholder="0"
                              {...form.register('manual_length_inches', { valueAsNumber: true })}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                <Collapsible defaultOpen={false} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 px-0 text-sm font-semibold [&:not(:hover)[data-state=open]]:bg-transparent"
                      >
                        <ChevronRight className="h-4 w-4 transition-transform duration-200 [[data-state=open]_&]:rotate-90" />
                        Pricing & Fees
                      </Button>
                    </CollapsibleTrigger>
                  </div>

                  <CollapsibleContent className="space-y-5 pt-2">
                  <div className="flex items-center gap-10">
                    <Label className="text-xs font-medium w-32 shrink-0">
                      {itemTypeLabel} Price
                    </Label>
                    <div className="flex-1 space-y-1">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...form.register('boat_price', { valueAsNumber: true })}
                      />
                      {form.formState.errors.boat_price && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.boat_price.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-10">
                    <Label className="text-xs font-medium w-32 shrink-0">Trailer Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      {...form.register('trailer_price', { valueAsNumber: true })}
                      className="flex-1"
                    />
                  </div>

                  <div className="flex items-center gap-10">
                    <Label className="text-xs font-medium w-32 shrink-0">Accessories</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      {...form.register('accessories_total', { valueAsNumber: true })}
                      className="flex-1"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-5">
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">Trade Allowance</Label>
                      <div className="flex-1 space-y-1">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...form.register('trade_in_value', { valueAsNumber: true })}
                          className="flex-1"
                          disabled={includeTradeInDetails}
                        />
                        {includeTradeInDetails && (
                          <p className="text-xs text-muted-foreground">
                            Trade allowance is calculated from the trade-in details below.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-5">
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">Sales Tax</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...form.register('tax_amount', { valueAsNumber: true })}
                        className="flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">Trailer Tax</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...form.register('trailer_tax', { valueAsNumber: true })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-5">
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">Doc Fee</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...form.register('doc_fee', { valueAsNumber: true })}
                        className="flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">Freight Fee</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...form.register('freight_fee', { valueAsNumber: true })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-5">
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">Prep Fee</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...form.register('prep_fee', { valueAsNumber: true })}
                        className="flex-1"
                      />
                    </div>
                    {canViewCost && (
                      <div className="flex items-center gap-10">
                        <Label className="text-xs font-medium w-32 shrink-0">{itemTypeLabel} Cost</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...form.register('boat_cost', { valueAsNumber: true })}
                          className="flex-1"
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-5">
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">Boat Registration</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...form.register('boat_registration_fee', { valueAsNumber: true })}
                        className="flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">Trailer Registration</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...form.register('trailer_registration_fee', { valueAsNumber: true })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="bg-accent/30 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Price</span>
                      <span className="font-medium">
                        {formatCurrency(price, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Trade Equity</span>
                      <span className="font-medium">
                        {formatCurrency(tradeEquity, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Fees & Taxes</span>
                      <span className="font-medium">
                        {formatCurrency(feesAndTaxes, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-base font-bold">
                      <span>Total</span>
                      <span className="text-primary">
                        {formatCurrency(total, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                <Collapsible defaultOpen={false} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 px-0 text-sm font-semibold [&:not(:hover)[data-state=open]]:bg-transparent"
                      >
                        <ChevronRight className="h-4 w-4 transition-transform duration-200 [[data-state=open]_&]:rotate-90" />
                        Trade-In Details
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent className="space-y-5 pt-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="quote_trade_in_details"
                      checked={includeTradeInDetails}
                      onCheckedChange={(checked) => {
                        const enabled = checked === true;
                        setIncludeTradeInDetails(enabled);
                        if (!enabled) {
                          form.setValue('trade_in_year', undefined, { shouldDirty: true });
                          form.setValue('trade_in_make', '', { shouldDirty: true });
                          form.setValue('trade_in_model', '', { shouldDirty: true });
                          form.setValue('trade_in_vin_hin', '', { shouldDirty: true });
                          form.setValue('trade_in_acv', undefined, { shouldDirty: true });
                          form.setValue('trade_in_allowance', undefined, { shouldDirty: true });
                          form.setValue('trade_in_payoff', undefined, { shouldDirty: true });
                        } else {
                          const currentTradeValue = form.getValues('trade_in_value');
                          if (!form.getValues('trade_in_allowance') && currentTradeValue) {
                            form.setValue('trade_in_allowance', currentTradeValue, { shouldDirty: true });
                          }
                        }
                      }}
                    />
                    <label
                      htmlFor="quote_trade_in_details"
                      className="text-sm font-normal leading-none"
                    >
                      Include trade-in details for this quote
                    </label>
                  </div>

                  {includeTradeInDetails && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">Year</Label>
                          <Input
                            type="number"
                            placeholder="2019"
                            {...form.register('trade_in_year', { valueAsNumber: true })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">Make</Label>
                          <Input
                            placeholder="Sea Ray"
                            {...form.register('trade_in_make')}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">Model</Label>
                          <Input
                            placeholder="SPX 190"
                            {...form.register('trade_in_model')}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">VIN / HIN</Label>
                          <Input
                            placeholder="US-ABC12345B919"
                            {...form.register('trade_in_vin_hin')}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">ACV</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            {...form.register('trade_in_acv', { valueAsNumber: true })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">Trade Allowance</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            {...form.register('trade_in_allowance', { valueAsNumber: true })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium">Trade Payoff</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            {...form.register('trade_in_payoff', { valueAsNumber: true })}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Trade allowance used in totals:{' '}
                        {formatCurrency(tradeInDetailsValue, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  )}
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                <Collapsible defaultOpen={false} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 px-0 text-sm font-semibold [&:not(:hover)[data-state=open]]:bg-transparent"
                      >
                        <ChevronRight className="h-4 w-4 transition-transform duration-200 [[data-state=open]_&]:rotate-90" />
                        Financing
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent className="space-y-5 pt-2">

                  <div className="grid grid-cols-2 gap-5">
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">Down Payment</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...form.register('down_payment', { valueAsNumber: true })}
                        className="flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">Loan Term (months)</Label>
                      <Input
                        type="number"
                        placeholder="180"
                        {...form.register('loan_term_months', { valueAsNumber: true })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-10">
                    <Label className="text-xs font-medium w-32 shrink-0">Interest Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="5.99"
                      {...form.register('interest_rate', { valueAsNumber: true })}
                      className="flex-1"
                    />
                  </div>

                  {monthlyPayment !== null && (
                    <div className="bg-primary/5 rounded-lg p-4 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Estimated Monthly Payment</p>
                      <p className="text-2xl font-bold text-primary">
                        {formatCurrency(monthlyPayment, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        for {loanTermMonths} months
                      </p>
                    </div>
                  )}
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                <Collapsible defaultOpen={false} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 px-0 text-sm font-semibold [&:not(:hover)[data-state=open]]:bg-transparent"
                      >
                        <ChevronRight className="h-4 w-4 transition-transform duration-200 [[data-state=open]_&]:rotate-90" />
                        Validity
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent className="space-y-5 pt-2">

                  <div className="grid grid-cols-2 gap-5">
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">Valid From</Label>
                      <Input
                        type="date"
                        {...form.register('valid_from', {
                          setValueAs: (value) => (value === '' ? undefined : value),
                        })}
                        className="flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-10">
                      <Label className="text-xs font-medium w-32 shrink-0">
                        Valid Until
                      </Label>
                      <div className="flex-1 space-y-1">
                        <Input
                          type="date"
                          {...form.register('valid_until', {
                            setValueAs: (value) => (value === '' ? undefined : value),
                          })}
                          className="flex-1"
                        />
                        {form.formState.errors.valid_until && (
                          <p className="text-xs text-destructive">
                            {form.formState.errors.valid_until.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  </CollapsibleContent>
                </Collapsible>
                </div>
            </SheetBody>

            {/* Footer */}
            <SheetFooter className="flex-row border-t py-2.5 px-5 border-border gap-2.5">
              <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isPending}>
                {isPending ? 'Saving...' : isEditing ? 'Update Quote' : 'Create Quote'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <QuickCreateCustomerDialog
        open={showQuickCreate}
        onOpenChange={setShowQuickCreate}
        onSuccess={handleQuickCreateSuccess}
      />
      <QuickCreateCustomerDialog
        open={showCoBuyerQuickCreate}
        onOpenChange={setShowCoBuyerQuickCreate}
        onSuccess={handleCoBuyerQuickCreateSuccess}
      />
    </>
  );
}
